import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getMyAccount, updateAccountSettings, transferAccountOwnership,
  deleteEntireAccount, listTeamMembers,
} from "@/lib/settings.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/settings/account")({
  component: AccountSettings,
});

function AccountSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fetchAccount = useServerFn(getMyAccount);
  const saveSettings = useServerFn(updateAccountSettings);
  const transfer = useServerFn(transferAccountOwnership);
  const deleteAcct = useServerFn(deleteEntireAccount);
  const fetchMembers = useServerFn(listTeamMembers);

  const { data, isLoading } = useQuery({ queryKey: ["my-account"], queryFn: () => fetchAccount() });
  const account = data?.account;
  const myRole = data?.myRole;

  const { data: teamData } = useQuery({
    queryKey: ["team", account?.id],
    queryFn: () => fetchMembers({ data: { account_id: account!.id } }),
    enabled: !!account?.id,
  });

  const [name, setName] = useState("");
  const [shareMetrics, setShareMetrics] = useState(true);
  const [allowPatterns, setAllowPatterns] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [newOwner, setNewOwner] = useState<string>("");

  useEffect(() => {
    if (account) {
      setName(account.name ?? "");
      setShareMetrics(account.privacy_share_anonymized_metrics ?? true);
      setAllowPatterns(account.privacy_allow_pattern_contributions ?? true);
    }
  }, [account]);

  if (isLoading || !account) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const isOwner = myRole === "owner";
  const otherMembers = (teamData?.members ?? []).filter((m: any) => m.user_id !== (account as any)._currentUserId);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings({
        data: {
          account_id: account.id,
          name, privacy_share_anonymized_metrics: shareMetrics,
          privacy_allow_pattern_contributions: allowPatterns,
        },
      });
      toast.success("Account updated");
      await queryClient.invalidateQueries({ queryKey: ["my-account"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!newOwner) return;
    try {
      await transfer({ data: { account_id: account.id, new_owner_user_id: newOwner } });
      toast.success("Ownership transferred");
      await queryClient.invalidateQueries();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAcct({ data: { account_id: account.id, confirm_name: confirmName } });
      await supabase.auth.signOut();
      navigate({ to: "/login" });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Account details</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Plan: <span className="font-medium text-foreground">{account.plan.replace("_", " ")}</span>
          {account.is_admin_comped && <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">Comped</span>}
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="acct-name">Account name</Label>
            <Input id="acct-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!isOwner && myRole !== "admin"} />
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Privacy</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Control how your data contributes to community intelligence. We never share identifiable customer data.
        </p>

        <div className="mt-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Share anonymized metrics</p>
              <p className="text-xs text-muted-foreground">Helps benchmark your performance against the SuperAffiliate community.</p>
            </div>
            <Switch checked={shareMetrics} onCheckedChange={setShareMetrics} disabled={!isOwner && myRole !== "admin"} />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Contribute to pattern library</p>
              <p className="text-xs text-muted-foreground">Patterns you discover may help others (always anonymized, opt-in per pattern).</p>
            </div>
            <Switch checked={allowPatterns} onCheckedChange={setAllowPatterns} disabled={!isOwner && myRole !== "admin"} />
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={handleSave} disabled={saving || (!isOwner && myRole !== "admin")}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes
          </Button>
        </div>
      </section>

      {isOwner && (
        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-base font-medium">Transfer ownership</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hand over ownership to another team member. You'll be demoted to admin.
          </p>
          {otherMembers.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No other members yet, invite teammates on the Team tab first.</p>
          ) : (
            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <Label>New owner</Label>
                <Select value={newOwner} onValueChange={setNewOwner}>
                  <SelectTrigger><SelectValue placeholder="Select a member" /></SelectTrigger>
                  <SelectContent>
                    {otherMembers.map((m: any) => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.profiles?.full_name ?? m.profiles?.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" disabled={!newOwner} onClick={handleTransfer}>Transfer</Button>
            </div>
          )}
        </section>
      )}

      {isOwner && (
        <section className="rounded-xl border border-destructive/30 bg-card p-6">
          <h2 className="text-base font-medium text-destructive">Delete account</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Permanently deletes the entire account, all workspaces, team members, and historical data. This cannot be undone.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="mt-4" size="sm">Delete account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{account.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  Type the account name <span className="font-mono">{account.name}</span> to confirm.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={account.name} />
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={confirmName.trim() !== account.name.trim()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete forever
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}
    </div>
  );
}
